import { inventoryDefinition, inventoryView } from './xrInventoryLayout'
import { flattenText, xrText } from './xrText'

export type XRInventoryAssets = { guiTexture: (path: string) => string; itemTexture: (item: any) => Promise<string | null> }

export class XRInventoryCanvas {
  constructor(private assets: XRInventoryAssets = { guiTexture: path => '/jerpg-pack/assets/minecraft/textures/' + path.replace(/^.*textures\//, ''), itemTexture: async () => null }) {}
  private images = new Map<string, HTMLImageElement | null>()
  private itemTextures = new Map<string, string | null>()
  private itemRetryAt = new Map<string, number>()
  private titleKey = ''
  private ready = false
  private loadError = ''

  private image(url: string) {
    if (!this.images.has(url)) {
      this.images.set(url, null)
      const image = new Image()
      image.onload = () => { this.images.set(url, image) }
      image.src = url
    }
    return this.images.get(url)
  }

  private itemImage(item: any) {
    const key = `${item.name}:${JSON.stringify(item.nbt)}:${JSON.stringify(item.components)}`
    if (!this.itemTextures.has(key) || (this.itemTextures.get(key) === null && performance.now() > (this.itemRetryAt.get(key) ?? 0))) {
      this.itemTextures.set(key, null)
      // Resource-pack atlases may finish after the inventory first opens.
      this.itemRetryAt.set(key, performance.now() + 2000)
      void this.assets.itemTexture(item).then(url => { this.itemTextures.set(key, url) }).catch(() => {})
    }
    const url = this.itemTextures.get(key)
    return url ? this.image(url) : null
  }

  drawItemIcon(ctx: CanvasRenderingContext2D, item: any, x: number, y: number, size: number) {
    if (!item) return
    const image = this.itemImage(item)
    if (image) { ctx.save(); ctx.imageSmoothingEnabled = false; ctx.drawImage(image, x, y, size, size); ctx.restore() }
  }

  paint(ctx: CanvasRenderingContext2D, win: any, container: any, selected: number | null) {
    const definition = inventoryDefinition(container)
    const title = container?.title ?? { text: definition.title, color: '#404040' }
    const key = JSON.stringify(title)
    if (key !== this.titleKey) {
      this.titleKey = key
      this.ready = false
      this.loadError = ''
      void xrText.prepare(title).then(() => {
        if (key !== this.titleKey) return
        this.ready = true
        this.loadError = flattenText(title).map(run => xrText.failures.get(run.font)).filter(Boolean).join('; ')
      })
    }
    const jerpg = flattenText(title).some(run => run.font.startsWith('jerpg:'))
    ctx.save()
    ctx.translate(inventoryView.x, inventoryView.y)
    ctx.scale(inventoryView.scale, inventoryView.scale)
    ctx.imageSmoothingEnabled = false
    const background = this.image(this.assets.guiTexture(definition.backgroundTexture))
    if (background) {
      if (definition.containerRows != null) {
        const top = definition.containerRows * 18 + 17
        ctx.drawImage(background, 0, 0, 176, top, 0, 0, 176, top)
        ctx.drawImage(background, 0, 125, 176, 97, 0, top, 176, 97)
      } else ctx.drawImage(background, 0, 0, definition.backgroundWidth, definition.backgroundHeight, 0, 0, definition.backgroundWidth, definition.backgroundHeight)
    } else {
      ctx.fillStyle = '#c6c6c6'
      ctx.fillRect(0, 0, definition.backgroundWidth, definition.backgroundHeight)
      for (const slot of definition.slots) { ctx.fillStyle = '#777'; ctx.fillRect(slot.x, slot.y, 18, 18) }
    }
    // Native title origin is x=8, top=6 (baseline=13). Phone's -8 spacer
    // and bitmap ascent=13 place its canvas exactly at inventory origin.
    if (this.ready) xrText.draw(ctx, title, 8 + (definition.titleOffset?.x ?? 0), 6 + (definition.titleOffset?.y ?? 0))
    for (const slot of definition.slots) {
      const item = win.slots[slot.index]
      if (item) {
        const image = this.itemImage(item)
        if (image) ctx.drawImage(image, slot.x + 1, slot.y + 1, 16, 16)
        else if (!jerpg) { ctx.fillStyle = '#222'; ctx.font = '5px sans-serif'; ctx.fillText(item.displayName, slot.x + 1, slot.y + 10, 16) }
        if (item.count > 1) {
          ctx.font = 'bold 7px monospace'; ctx.textAlign = 'right'; ctx.fillStyle = '#000'; ctx.fillText(String(item.count), slot.x + 18, slot.y + 17)
          ctx.fillStyle = '#fff'; ctx.fillText(String(item.count), slot.x + 17, slot.y + 16); ctx.textAlign = 'left'
        }
      }
      if (slot.index === selected) { ctx.fillStyle = 'rgba(255,255,255,0.28)'; ctx.fillRect(slot.x, slot.y, 18, 18); ctx.strokeStyle = '#aaffee'; ctx.lineWidth = 0.5; ctx.strokeRect(slot.x, slot.y, 18, 18) }
    }
    ctx.restore()
    if (!this.ready || this.loadError) {
      ctx.fillStyle = this.loadError ? '#ffb1a4' : '#f2dfaa'; ctx.font = '21px sans-serif'
      ctx.fillText(this.loadError ? `JERPG UI asset error: ${this.loadError}` : 'Loading server UI artwork…', 44, 1084, 940)
    }
    return definition
  }
}
